export function parseAwsJson(value) {
  if (value == null) return null
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return null
    }
  }
  return value
}

/**
 * Unwraps standard Yandle API envelopes: { status, data: { items } } or { status, data }.
 */
export function unwrapApiPayload(raw) {
  const parsed = parseAwsJson(raw)
  if (parsed == null) return null
  if (typeof parsed !== 'object') return parsed

  if (parsed.status === 'ERROR') {
    throw new Error(parsed.message || 'Request failed.')
  }

  if (parsed.status === 'SUCCESS' && parsed.data != null) {
    const { data } = parsed
    const items = data.items

    if (Array.isArray(items) && items.length) {
      return items[0]
    }

    if (items && typeof items === 'object' && !Array.isArray(items)) {
      if (items.userDetails || items.orgDetails) {
        return {
          userDetails: items.userDetails,
          orgDetails: items.orgDetails,
        }
      }
      return items
    }

    if (typeof data === 'object' && !Array.isArray(data)) {
      return data
    }
    return data
  }

  return parsed
}

function extractListFromNode(node) {
  if (node == null) return []

  const parsed = parseAwsJson(node)
  if (Array.isArray(parsed)) return parsed

  if (typeof parsed !== 'object') return []

  if (Array.isArray(parsed.items)) return parsed.items
  if (Array.isArray(parsed.Items)) return parsed.Items
  if (Array.isArray(parsed.calls)) return parsed.calls
  if (Array.isArray(parsed.call_history)) return parsed.call_history
  if (Array.isArray(parsed.users)) return parsed.users
  if (Array.isArray(parsed.transactions)) return parsed.transactions
  if (Array.isArray(parsed.org_transactions)) return parsed.org_transactions
  if (Array.isArray(parsed.contact_us)) return parsed.contact_us
  if (Array.isArray(parsed.contactUs)) return parsed.contactUs
  if (Array.isArray(parsed.reachouts)) return parsed.reachouts
  if (Array.isArray(parsed.credit_requests)) return parsed.credit_requests
  if (Array.isArray(parsed.creditRequests)) return parsed.creditRequests
  if (Array.isArray(parsed.credit_request_list)) return parsed.credit_request_list
  if (Array.isArray(parsed.CreditRequests)) return parsed.CreditRequests
  if (Array.isArray(parsed.unique_caller_stats)) return parsed.unique_caller_stats
  if (Array.isArray(parsed.uniqueCallerStats)) return parsed.uniqueCallerStats
  if (Array.isArray(parsed.caller_stats)) return parsed.caller_stats
  if (Array.isArray(parsed.unique_callers)) return parsed.unique_callers
  if (Array.isArray(parsed.outbound_actions)) return parsed.outbound_actions
  if (Array.isArray(parsed.outboundActions)) return parsed.outboundActions
  if (Array.isArray(parsed.actions)) return parsed.actions
  if (Array.isArray(parsed.rule_sets)) return parsed.rule_sets
  if (Array.isArray(parsed.ruleSets)) return parsed.ruleSets
  if (Array.isArray(parsed.api_keys)) return parsed.api_keys
  if (Array.isArray(parsed.apiKeys)) return parsed.apiKeys
  if (Array.isArray(parsed.accounts)) return parsed.accounts
  if (Array.isArray(parsed.email_accounts)) return parsed.email_accounts
  if (Array.isArray(parsed.emailAccounts)) return parsed.emailAccounts
  if (Array.isArray(parsed.emails)) return parsed.emails
  if (Array.isArray(parsed.labels)) return parsed.labels
  if (Array.isArray(parsed.filters)) return parsed.filters
  if (Array.isArray(parsed.messages)) return parsed.messages
  if (Array.isArray(parsed.attachments)) return parsed.attachments
  if (Array.isArray(parsed.edit_history)) return parsed.edit_history
  if (Array.isArray(parsed.editHistory)) return parsed.editHistory
  if (Array.isArray(parsed.edits)) return parsed.edits
  if (Array.isArray(parsed.history)) return parsed.history
  if (Array.isArray(parsed.draft_versions)) return parsed.draft_versions
  if (Array.isArray(parsed.draftVersions)) return parsed.draftVersions
  if (Array.isArray(parsed.versions)) return parsed.versions

  if (Array.isArray(parsed.images)) return parsed.images
  if (Array.isArray(parsed.website_images)) return parsed.website_images
  if (Array.isArray(parsed.websiteImages)) return parsed.websiteImages
  if (Array.isArray(parsed.files)) return parsed.files
  if (Array.isArray(parsed.assets)) return parsed.assets

  if (Array.isArray(parsed.on_call_actions)) return parsed.on_call_actions
  if (Array.isArray(parsed.onCallActions)) return parsed.onCallActions
  if (Array.isArray(parsed.business_queries)) return parsed.business_queries
  if (Array.isArray(parsed.businessQueries)) return parsed.businessQueries
  if (Array.isArray(parsed.queries)) return parsed.queries
  if (Array.isArray(parsed.requests)) return parsed.requests
  if (Array.isArray(parsed.leads)) return parsed.leads
  if (Array.isArray(parsed.Leads)) return parsed.Leads
  if (Array.isArray(parsed.lead_list)) return parsed.lead_list
  if (Array.isArray(parsed.leadList)) return parsed.leadList
  if (Array.isArray(parsed.contexts)) return parsed.contexts
  if (Array.isArray(parsed.Contexts)) return parsed.Contexts
  if (Array.isArray(parsed.context_list)) return parsed.context_list
  if (Array.isArray(parsed.contextList)) return parsed.contextList

  return extractListFromNode(parsed.data)
}

export function unwrapApiList(raw) {
  const parsed = parseAwsJson(raw)
  if (parsed == null) return []

  if (Array.isArray(parsed)) return parsed

  if (typeof parsed !== 'object') return []

  const status = String(parsed.status ?? parsed.Status ?? '')
    .trim()
    .toUpperCase()

  if (status === 'ERROR' || status === 'FAIL' || status === 'FAILED') {
    throw new Error(parsed.message || 'Request failed.')
  }

  const fromData = extractListFromNode(parsed.data)
  if (fromData.length) return fromData

  const fromItems = extractListFromNode(parsed.items ?? parsed.Items)
  if (fromItems.length) return fromItems

  const fromCalls = extractListFromNode(
    parsed.calls ??
      parsed.call_history ??
      parsed.users ??
      parsed.transactions ??
      parsed.org_transactions ??
      parsed.contact_us ??
      parsed.contactUs ??
      parsed.reachouts ??
      parsed.credit_requests ??
      parsed.creditRequests ??
      parsed.credit_request_list ??
      parsed.CreditRequests ??
      parsed.unique_caller_stats ??
      parsed.uniqueCallerStats ??
      parsed.caller_stats ??
      parsed.unique_callers ??
      parsed.outbound_actions ??
      parsed.outboundActions ??
      parsed.actions ??
      parsed.rule_sets ??
      parsed.ruleSets ??
      parsed.api_keys ??
      parsed.apiKeys ??
      parsed.accounts ??
      parsed.email_accounts ??
      parsed.emailAccounts ??
      parsed.emails ??
      parsed.labels ??
      parsed.filters ??
      parsed.messages ??
      parsed.attachments ??
      parsed.edit_history ??
      parsed.editHistory ??
      parsed.edits ??
      parsed.history ??
      parsed.draft_versions ??
      parsed.draftVersions ??
      parsed.versions ??
      parsed.on_call_actions ??
      parsed.onCallActions ??
      parsed.business_queries ??
      parsed.businessQueries ??
      parsed.queries ??
      parsed.requests ??
      parsed.leads ??
      parsed.Leads ??
      parsed.lead_list ??
      parsed.leadList ??
      parsed.contexts ??
      parsed.Contexts ??
      parsed.context_list ??
      parsed.contextList,
  )
  if (fromCalls.length) return fromCalls

  if (parsed.success === true || parsed.ok === true) return []

  if (status === 'SUCCESS') return []

  return []
}
